import { X, Edit } from "lucide-react";
import { useEffect, useRef, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/app/_module/components/ui/button";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { toast } from "@/app/_module/components/ui/use-toast";
import { extractPhoneNumber, formatPhoneNumber } from "@/app/_module/utils/formatters";
import { getStateCode } from "@/app/_module/utils/state-mapping";

import { LeadDetailsLayout } from "./LeadDetailsLayout";
import { LeadActionButtons } from "./LeadActionButtons";
import { LEAD_FORM_DEFAULTS } from "../constants/form-defaults";
import type { Lead } from "../types/get-leads-type";
import { useLead, useUpdateLead, useLeadWithPolicies, useCreateBeneficiaries, useUpdateBeneficiary } from "../hooks/queries";
import { LeadTagChips } from "./LeadTagChips";
import {
  updateLeadSchema,
  type UpdateLeadSchema,
} from "../schema/lead-schema";

/**
 * Helper function for stable JSON stringification
 * Sorts object keys to ensure consistent comparison
 */
function stableStringify(obj: any): string {
  if (obj === null || obj === undefined) return String(obj);
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) {
    return '[' + obj.map(stableStringify).join(',') + ']';
  }
  const sortedKeys = Object.keys(obj).sort();
  const pairs = sortedKeys.map(key => `${JSON.stringify(key)}:${stableStringify(obj[key])}`);
  return '{' + pairs.join(',') + '}';
}

interface LeadDetailsPanelProps {
  leadId: string;
  onClose?: () => void;
  compact?: boolean;
  showCloseButton?: boolean;
  onSave?: () => void | Promise<void>;
  embedded?: boolean; // When true, prevents URL tab syncing (for modal/dialer contexts)
  hideCallButton?: boolean; // When true, hides the Call button (for Power Dialer context where we're already calling)
  onStatusChange?: (leadId: string, status: string) => void; // Called when lead status changes (for dialer disposition tracking)
  alwaysEditing?: boolean; // When true, always shows in edit mode (like dialer). When false, shows Edit button first (like lead page)
}

/**
 * Reusable Lead Details Panel
 * Shows full lead details with all tabs (Details, Policy, Notes, Call History, etc.)
 * Can be embedded in dialer sessions or shown as standalone panel
 */
export const LeadDetailsPanel = function ({
  leadId,
  onClose,
  compact = false,
  showCloseButton = true,
  onSave,
  embedded = false,
  hideCallButton = false,
  onStatusChange,
  alwaysEditing = true, // Default to true for backward compatibility (dialers always edit)
}: LeadDetailsPanelProps) {
  const { mutateAsync: updateLeadMutation, isPending: isUpdating } = useUpdateLead();
  const { mutateAsync: createBeneficiariesMutation } = useCreateBeneficiaries();
  const { mutateAsync: updateBeneficiaryMutation } = useUpdateBeneficiary();
  const { data: leadResponse, isLoading, error, refetch } = useLead(leadId || "");
  const { data: leadWithPolicies, isLoading: policiesLoading, refetch: refetchPolicies } = useLeadWithPolicies(leadId || "");

  // Extract lead data from response (handle both direct and wrapped formats)
  const lead = (leadResponse as any)?.data || leadResponse;

  // Editing state - when alwaysEditing is false, we show an Edit button first
  const [isEditing, setIsEditing] = useState(alwaysEditing);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const previousFormDataRef = useRef<string>('');
  const isSavingRef = useRef(false); // Lock to prevent concurrent saves
  const hasPopulatedFormRef = useRef(false); // Track if form has been populated initially
  const originalCustomFieldsRef = useRef<string>('{}'); // Track original customFields

  const form = useForm<UpdateLeadSchema>({
    resolver: zodResolver(updateLeadSchema),
    mode: "onChange",
    defaultValues: LEAD_FORM_DEFAULTS,
  });

  // Populate form only once when lead data first arrives (prevents overwriting user edits on refetch)
  useEffect(() => {
    if (lead && form && !hasPopulatedFormRef.current) {
      hasPopulatedFormRef.current = true;

      const formKeys = Object.keys(
        form.getValues()
      ) as (keyof typeof LEAD_FORM_DEFAULTS)[];

      formKeys.forEach((fieldKey) => {
        // Skip beneficiaries - they're loaded separately
        if (fieldKey === 'beneficiaries') return;

        const value = (lead as any)?.[fieldKey];

        // Handle customFields object specially
        if (fieldKey === 'customFields') {
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            form.setValue(fieldKey, value);
            originalCustomFieldsRef.current = stableStringify(value);
          } else {
            form.setValue(fieldKey, {});
            originalCustomFieldsRef.current = stableStringify({});
          }
        } else if (value !== null && value !== undefined) {
          // Normalize state values to 2-letter codes so the dropdown can match
          if (fieldKey === 'state' && typeof value === 'string' && value) {
            form.setValue(fieldKey, getStateCode(value));
          } else {
            form.setValue(fieldKey, value || "");
          }
        } else {
          // Set empty string for null/undefined values
          form.setValue(fieldKey, "");
        }
      });

      // Mark initial load complete and store initial form data
      setTimeout(() => {
        isInitialLoadRef.current = false;
        previousFormDataRef.current = stableStringify(form.getValues());
      }, 500);
    }
  }, [lead, form]);

  // Auto-save function (silent, no toast on success)
  const performAutoSave = useCallback(async () => {
    // Prevent concurrent saves and skip during initial load
    if (!leadId || isInitialLoadRef.current || isSavingRef.current) return;

    const formData = form.getValues();
    const { beneficiaries, ...leadData } = formData;

    // Ensure customFields is always a plain object
    let customFieldsValue = leadData.customFields;
    if (!customFieldsValue || typeof customFieldsValue !== 'object' || Array.isArray(customFieldsValue)) {
      customFieldsValue = {};
    }

    // Filter out empty strings and undefined values for cleaner data
    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(leadData)) {
      if (key === 'customFields') {
        // Only include customFields if it has actually changed from the original
        const currentCustomFieldsStr = stableStringify(customFieldsValue);
        if (currentCustomFieldsStr !== originalCustomFieldsRef.current) {
          cleanData.customFields = customFieldsValue;
        }
      } else if (value !== undefined && value !== null && value !== "") {
        cleanData[key] = value;
      }
    }

    // Clean phone number if present
    if (cleanData.phone && typeof cleanData.phone === 'string') {
      cleanData.phone = extractPhoneNumber(cleanData.phone);
    }

    // Set lock
    isSavingRef.current = true;

    try {
      setIsAutoSaving(true);

      // Save lead data
      await updateLeadMutation({ leadId, data: cleanData });

      // Handle beneficiaries if present and have changes
      if (beneficiaries && beneficiaries.length > 0) {
        const newBeneficiaries = beneficiaries.filter((b: any) => !b.id && b.name && b.name.trim() !== '');
        const existingBeneficiaries = beneficiaries.filter((b: any) => b.id && b.name && b.name.trim() !== '');

        // Create new beneficiaries and update existing ones in parallel
        const promises: Promise<any>[] = [];

        if (newBeneficiaries.length > 0) {
          promises.push(createBeneficiariesMutation({
            leadId,
            beneficiaries: newBeneficiaries,
          }));
        }

        // Update existing beneficiaries in parallel
        for (const beneficiary of existingBeneficiaries) {
          if (beneficiary.id) {
            promises.push(updateBeneficiaryMutation({
              leadId,
              beneficiaryId: beneficiary.id,
              data: {
                name: beneficiary.name,
                relationship: beneficiary.relationship,
                percentage: beneficiary.percentage,
                dob: beneficiary.dob,
                ssn: beneficiary.ssn,
              },
            }));
          }
        }

        // Execute all mutations in parallel
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }

      setLastSavedAt(new Date());

      // Update previousFormDataRef to current form state
      previousFormDataRef.current = stableStringify(form.getValues());

      // Update originalCustomFieldsRef so future saves only include customFields if they change again
      const currentCustomFields = form.getValues('customFields') || {};
      originalCustomFieldsRef.current = stableStringify(currentCustomFields);

      // Notify parent if status changed (for dialer disposition tracking)
      if (cleanData.status && onStatusChange) {
        onStatusChange(leadId, cleanData.status);
      }
    } catch (error) {
      // Silent fail for auto-save
      console.error('Auto-save failed:', error);
    } finally {
      setIsAutoSaving(false);
      isSavingRef.current = false; // Release lock
    }
  }, [leadId, form, updateLeadMutation, createBeneficiariesMutation, updateBeneficiaryMutation]);

  // Watch for form changes and trigger auto-save with debounce
  const performAutoSaveRef = useRef(performAutoSave);
  performAutoSaveRef.current = performAutoSave;

  useEffect(() => {
    const subscription = form.watch((formData) => {
      // Skip during initial load or while saving
      if (isInitialLoadRef.current || isSavingRef.current) return;

      // Check if data actually changed using stable stringify
      const currentData = stableStringify(formData);
      if (currentData === previousFormDataRef.current) return;

      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for auto-save (2 second debounce)
      autoSaveTimeoutRef.current = setTimeout(() => {
        performAutoSaveRef.current();
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form]);

  // Cleanup on unmount - save any pending changes
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        // Perform final save on unmount if there are pending changes
        const currentData = stableStringify(form.getValues());
        if (currentData !== previousFormDataRef.current && !isInitialLoadRef.current) {
          performAutoSaveRef.current();
        }
      }
    };
  }, [form]);

  // Show loading state
  if (isLoading || policiesLoading) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'h-64' : 'h-96'} bg-white rounded-lg border`}>
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state
  if (error || !lead) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'h-64' : 'h-96'} bg-white rounded-lg border`}>
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading lead details</p>
          {onClose && (
            <Button onClick={onClose} variant="outline" size="sm">
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    const formData = form.getValues();

    const cleanData = Object.fromEntries(
      Object.entries(formData).filter(([key, value]) => {
        if (key === 'customFields') return true;
        if (key === 'beneficiaries') return false; // Handle separately
        return value !== undefined && value !== null && value !== "";
      })
    );

    if (cleanData.phone && typeof cleanData.phone === 'string') {
      cleanData.phone = extractPhoneNumber(cleanData.phone);
    }

    try {
      // Save lead data
      await updateLeadMutation({ leadId, data: cleanData });

      // Save beneficiaries if they exist in the form
      const beneficiaries = formData.beneficiaries;

      if (beneficiaries && Array.isArray(beneficiaries) && beneficiaries.length > 0) {
        // Separate existing beneficiaries (with id) from new ones (without id)
        const existingBeneficiaries = beneficiaries.filter((b: any) => b.id && b.name && b.name.trim() !== '');
        const newBeneficiaries = beneficiaries.filter((b: any) => !b.id && b.name && b.name.trim() !== '');

        // Update existing beneficiaries
        if (existingBeneficiaries.length > 0) {
          await Promise.all(
            existingBeneficiaries.map((b: any) =>
              updateBeneficiaryMutation({
                leadId,
                beneficiaryId: b.id,
                data: {
                  name: b.name,
                  relationship: b.relationship,
                  percentage: b.percentage,
                  dob: b.dob,
                  ssn: b.ssn,
                },
              })
            )
          );
        }

        // Create new beneficiaries
        if (newBeneficiaries.length > 0) {
          await createBeneficiariesMutation({
            leadId,
            beneficiaries: newBeneficiaries,
          });
        }
      }

      await Promise.all([refetch(), refetchPolicies()]);
      toast({
        title: "Lead Updated",
        description: "Lead details have been updated successfully",
      });

      // Call onSave callback if provided (to refresh parent component)
      if (onSave) {
        await onSave();
      }
    } catch (error) {

      toast({
        title: "Update Failed",
        description: "Failed to update lead. Please try again.",
        variant: "destructive",
      });
    }
  };

  const hasPolicies = (leadWithPolicies?.policies && leadWithPolicies.policies.length > 0) || false;
  const policies = leadWithPolicies?.policies || [];
  const policyCount = policies.length;

  return (
    <div className={`bg-white rounded-lg border shadow-sm ${compact ? 'max-h-[600px]' : 'max-h-[800px]'} overflow-hidden flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b bg-gray-50 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{(lead as any)?.name || 'Lead Details'}</h3>
            <p className="text-sm text-gray-600">{(lead as any)?.phone ? formatPhoneNumber((lead as any).phone) : 'No phone number'}</p>
            {/* Last call — right where agents look before dialing, so nobody
                double-calls the same person twice in a day (Enes' request) */}
            <p className="text-xs text-gray-500 mt-0.5">
              Last call:{' '}
              {(lead as any)?.lastCall
                ? new Date((lead as any).lastCall).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })
                : 'never'}
            </p>
            {/* Tags incl. automatic status history ("Was: Sold & Approved") */}
            {(lead as any)?.tags?.length > 0 && (
              <div className="mt-1.5">
                <LeadTagChips leadId={String((lead as any)?.id ?? '')} tags={(lead as any).tags} compact />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Show Edit button when not in editing mode, Save button when editing */}
            {!isEditing ? (
              <Button
                onClick={() => setIsEditing(true)}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            ) : (
              <>
                {/* Auto-save status indicator */}
                <div className="text-xs text-gray-500 mr-2">
                  {isAutoSaving ? (
                    <span className="text-primary">Saving...</span>
                  ) : lastSavedAt ? (
                    <span className="text-green-600">✓ Auto-saved</span>
                  ) : null}
                </div>
                <Button
                  onClick={handleSave}
                  disabled={isUpdating || isAutoSaving}
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isUpdating || isAutoSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
            {showCloseButton && onClose && (
              <Button
                onClick={onClose}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {/* Action Buttons - Call, SMS, Email, Schedule */}
        <LeadActionButtons
          lead={lead as Lead}
          onEdit={() => {}}
          hideCallButton={hideCallButton}
          onStatusChange={onStatusChange}
        />
      </div>

      {/* Lead Details Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        <FormProvider {...form}>
          <LeadDetailsLayout
            isEditing={isEditing}
            leadId={leadId}
            hasPolicies={hasPolicies}
            policies={policies}
            policyCount={policyCount}
            leadData={leadWithPolicies}
            embedded={embedded || compact}
            defaultTab="details"
            onStatusChange={onStatusChange ? async (id, status) => {
              // Wrap sync callback as async for LeadDetailsLayout compatibility
              onStatusChange(id, status);
            } : undefined}
          />
        </FormProvider>
      </div>
    </div>
  );
};
